import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalysisFormData, BackgroundAnalysisOptions, BackgroundAnalysisStatus } from '../types';
import { Brain, Zap, User, ChevronDown, ChevronUp, X, AlertCircle, Wand2 } from 'lucide-react';
import { getCsrfToken, getCurrentCsrfToken } from '../services/csrfService';
import { formatTime } from '../services/backgroundAnalysisService';
import { getABTestMode, saveSuggestedQuestions } from '../services/abTestService';
import { useLists } from '../contexts/ListsContext';
import { validateAndSendExpertStat } from '../services/expertValidationService';
import Disclaimer from './Disclaimer';
import FullScreenLoader from './FullScreenLoader';
import { getRandomExample } from '../utils/exampleQueries';

interface AnalysisFormProps {
  onSubmit: (data: AnalysisFormData, options?: BackgroundAnalysisOptions) => void;
  onCancel?: () => void;
  onAbortAnalysis?: () => void; // Callback для прерывания анализа
  isLoading?: boolean;
  prefilledDescription?: string;
  prefilledExpert?: string;
  prefilledPerson?: string;
  prefilledCustomExpert?: string;
  prefilledCustomPerson?: string;
  prefilledQuestion?: string;
  backgroundAnalysisStatus?: BackgroundAnalysisStatus | null;
  error?: string | null;
  onClearValidationError?: () => void; // Callback для очистки validation error статуса
  theme?: 'dark' | 'light';
}


export default function AnalysisForm({ onSubmit, onCancel, onAbortAnalysis, isLoading = false, prefilledDescription = '', prefilledExpert = '', prefilledPerson = '', prefilledCustomExpert = '', prefilledCustomPerson = '', prefilledQuestion = '', backgroundAnalysisStatus, error, onClearValidationError, theme = 'light' }: AnalysisFormProps) {
  const { t, i18n } = useTranslation('landing');
  const [formData, setFormData] = useState<AnalysisFormData>({
    description: prefilledDescription || '',
    expert: prefilledExpert || undefined,
    person: prefilledPerson || undefined,
    question: prefilledQuestion || undefined,
    gender: undefined,
    age: undefined,
    maritalStatus: undefined
  });
  const [csrfInitialized, setCsrfInitialized] = useState(false);
  const [showCustomExpert, setShowCustomExpert] = useState(false);
  const [customExpert, setCustomExpert] = useState(prefilledCustomExpert);
  const [showExpertSection, setShowExpertSection] = useState(true);
  const [showCustomPerson, setShowCustomPerson] = useState(false);
  const [customPerson, setCustomPerson] = useState(prefilledCustomPerson);
  const [showPersonSection, setShowPersonSection] = useState(false);
  const [lastSubmittedDescription, setLastSubmittedDescription] = useState('');
  
  // Состояния для модального окна с выбором вопроса
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [customQuestion, setCustomQuestion] = useState('');
  const [questionChoice, setQuestionChoice] = useState<string | null>(null);
  const [processedFailedTaskId, setProcessedFailedTaskId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const isFirstRender = useRef(true);
  const isExampleClick = useRef(false); // Флаг для отслеживания программного изменения через кнопку примера
  
  // Получаем списки из контекста
  const { experts, persons, loading: listsLoading } = useLists();
  
  // Ref для отслеживания последних примененных предзаполненных значений
  const lastAppliedPrefilledValues = useRef({
    description: prefilledDescription,
    expert: prefilledExpert,
    person: prefilledPerson,
    question: prefilledQuestion,
    customExpert: prefilledCustomExpert,
    customPerson: prefilledCustomPerson
  });
  

  
  // Регулярное выражение для проверки читаемых символов (аналогично backend)
  const readableCharPattern = /^[a-zA-Zа-яА-ЯёЁ0-9\s\-.,()]*$/;
  
  // Функция для валидации эксперта
  const validateExpert = (value: string): boolean => {
    return readableCharPattern.test(value);
  };
  
  // Функция для очистки невалидных символов
  const sanitizeExpert = (value: string): string => {
    return value.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\s\-.,()]/g, '');
  };
  
  const MAX_DESCRIPTION_LENGTH = 300;
  const MIN_DESCRIPTION_LENGTH = 15;
  const currentLength = formData.description?.length || 0;
  const trimmedLength = formData.description?.trim().length || 0;
  const isDescriptionValid = trimmedLength >= MIN_DESCRIPTION_LENGTH && currentLength <= MAX_DESCRIPTION_LENGTH;

  // Инициализация CSRF токена при загрузке компонента
  useEffect(() => {
    const initializeCsrf = async () => {
      // Проверяем, есть ли уже токен
      const currentToken = getCurrentCsrfToken();
      if (currentToken) {
        console.log('CSRF: Токен уже есть, пропускаем инициализацию');
        setCsrfInitialized(true);
        return;
      }

      try {
        console.log('CSRF: Инициализация токена в форме');
        await getCsrfToken();
        setCsrfInitialized(true);
      } catch (error) {
        console.warn('Не удалось инициализировать CSRF токен:', error);
        setCsrfInitialized(true); // Продолжаем работу без CSRF
      }
    };

    initializeCsrf();
  }, []);


  // Автоматическая прокрутка в начало страницы при монтировании компонента
  useEffect(() => {
    // Плавная прокрутка в начало страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Обновляем форму при изменении предзаполненных значений
  useEffect(() => {
    let hasChanges = false;
    const updates: Partial<AnalysisFormData> = {};
    
    // Проверяем, изменились ли предзаполненные значения с момента последнего применения
    if (prefilledDescription && prefilledDescription !== lastAppliedPrefilledValues.current.description) {
      updates.description = prefilledDescription;
      hasChanges = true;
      lastAppliedPrefilledValues.current.description = prefilledDescription;
    }
    
    if (prefilledExpert && prefilledExpert !== lastAppliedPrefilledValues.current.expert) {
      updates.expert = prefilledExpert;
      hasChanges = true;
      lastAppliedPrefilledValues.current.expert = prefilledExpert;
    }
    
    if (prefilledPerson && prefilledPerson !== lastAppliedPrefilledValues.current.person) {
      updates.person = prefilledPerson;
      hasChanges = true;
      lastAppliedPrefilledValues.current.person = prefilledPerson;
    }
    
    if (prefilledQuestion && prefilledQuestion !== lastAppliedPrefilledValues.current.question) {
      updates.question = prefilledQuestion;
      hasChanges = true;
      lastAppliedPrefilledValues.current.question = prefilledQuestion;
    }
    
    if (hasChanges) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
    
    // Обновляем custom значения
    if (prefilledCustomExpert && prefilledCustomExpert !== lastAppliedPrefilledValues.current.customExpert) {
      setCustomExpert(prefilledCustomExpert);
      lastAppliedPrefilledValues.current.customExpert = prefilledCustomExpert;
    }
    
    if (prefilledCustomPerson && prefilledCustomPerson !== lastAppliedPrefilledValues.current.customPerson) {
      setCustomPerson(prefilledCustomPerson);
      lastAppliedPrefilledValues.current.customPerson = prefilledCustomPerson;
    }
  }, [prefilledDescription, prefilledExpert, prefilledPerson, prefilledQuestion, prefilledCustomExpert, prefilledCustomPerson]);

  // Автоматически раскрываем секции, если есть предзаполненные значения
  useEffect(() => {
    // Раскрываем блок опций, если есть предзаполненные значения эксперта или личности
    if (prefilledExpert || prefilledPerson) {
      setShowExpertSection(true);
    }
    
    // Обрабатываем эксперта
    if (prefilledExpert) {
      if (prefilledExpert !== 'custom') {
        setShowCustomExpert(false);
      } else {
        setShowCustomExpert(true);
      }
    }
    
    // Обрабатываем личность
    if (prefilledPerson) {
      if (prefilledPerson !== 'custom') {
        setShowCustomPerson(false);
      } else {
        setShowCustomPerson(true);
      }
    }
  }, [prefilledExpert, prefilledPerson]);

  // Сбрасываем флаг первого рендера после монтирования компонента
  useEffect(() => {
    const timer = setTimeout(() => {
      isFirstRender.current = false;
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Обработка ошибки валидации с примерами из статуса фонового анализа
  useEffect(() => {
    if (
      backgroundAnalysisStatus?.status === 'failed' &&
      backgroundAnalysisStatus?.data?.type === 'validation_error' &&
      backgroundAnalysisStatus?.data?.examples &&
      backgroundAnalysisStatus.data.examples.length > 0 &&
      backgroundAnalysisStatus.taskId !== processedFailedTaskId &&
      !showQuestionModal && // Не показываем если модальное окно уже открыто
      !isExampleClick.current // Не запускаем анализ при программном изменении через кнопку примера
    ) {
      const abTestMode = getABTestMode();
      
      if (abTestMode === 'A') {
        // Режим A: показываем модальное окно с выбором вопроса
        setShowQuestionModal(true);
        setQuestionChoice(null);
        setCustomQuestion('');
        setProcessedFailedTaskId(backgroundAnalysisStatus.taskId);
      } else {
        // Режим B: сохраняем вопросы и запускаем анализ без вопроса
        const suggestedQuestions = {
          questions: backgroundAnalysisStatus.data.examples,
          originalRequest: formData.description || '',
          expert: formData.expert,
          person: formData.person,
          customExpert: formData.expert === 'custom' ? customExpert : undefined,
          customPerson: formData.person === 'custom' ? customPerson : undefined
        };
        
        saveSuggestedQuestions(suggestedQuestions);
        
        // Валидируем эксперта/личность перед автоматическим перезапуском
        validateAndSendExpertStat(
          formData.expert,
          formData.person,
          formData.expert === 'custom' ? customExpert : undefined,
          formData.person === 'custom' ? customPerson : undefined
        ).then(() => {
          // Проверяем флаг перед запуском анализа
          if (isExampleClick.current) {
            console.log('🔍 AnalysisForm - Пропускаем автозапуск анализа, так как это программное изменение');
            return;
          }
          
          // Запускаем анализ без вопроса после валидации
          const dataWithNoQuestion = {
            ...formData,
            question: undefined,
            noQuestion: true
          };
          
          // Используем setTimeout чтобы избежать проблем с зависимостями
          setTimeout(() => {
            // Дополнительная проверка флага перед отправкой
            if (!isExampleClick.current) {
              onSubmit(dataWithNoQuestion);
            }
          }, 0);
        }).catch((error) => {
          console.error('❌ AnalysisForm - Ошибка валидации при автоматическом перезапуске:', error);
          
          // Проверяем флаг перед запуском анализа
          if (isExampleClick.current) {
            console.log('🔍 AnalysisForm - Пропускаем автозапуск анализа, так как это программное изменение');
            return;
          }
          
          // В случае ошибки валидации все равно запускаем анализ
          const dataWithNoQuestion = {
            ...formData,
            question: undefined,
            noQuestion: true
          };
          
          // Используем setTimeout чтобы избежать проблем с зависимостями
          setTimeout(() => {
            // Дополнительная проверка флага перед отправкой
            if (!isExampleClick.current) {
              onSubmit(dataWithNoQuestion);
            }
          }, 0);
        });
        
        setProcessedFailedTaskId(backgroundAnalysisStatus.taskId);
      }
    }
  }, [backgroundAnalysisStatus, processedFailedTaskId, showQuestionModal, formData, customExpert, customPerson]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDescriptionValid) {
      try {
        // 1. Сначала валидируем эксперта/личность и отправляем статистику
        console.log('🔍 AnalysisForm - Начинаем валидацию эксперта/личности...');
        await validateAndSendExpertStat(
          formData.expert,
          formData.person,
          formData.expert === 'custom' ? customExpert : undefined,
          formData.person === 'custom' ? customPerson : undefined,
          i18n.language
        );
        
        // 2. Определяем финального эксперта
        let finalExpert = formData.expert;
        if (formData.expert === 'custom' && customExpert.trim()) {
          finalExpert = customExpert.trim();
        }
        
        // 3. Определяем финальную личность
        let finalPerson = formData.person;
        if (formData.person === 'custom' && customPerson.trim()) {
          finalPerson = customPerson.trim();
        }
        
        // Формируем финальное описание с персональными данными
        const baseDescription = formData.description?.trim() || '';
        const personalInfo = [];
        
        if (formData.gender) {
          personalInfo.push(`Пол: ${formData.gender}`);
        }
        if (formData.age) {
          personalInfo.push(`Возраст: ${formData.age} лет`);
        }
        if (formData.maritalStatus) {
          personalInfo.push(`Семейное положение: ${formData.maritalStatus}`);
        }
        
        const finalDescription = personalInfo.length > 0 
          ? `${baseDescription}\n\n${personalInfo.join('. ')}.`
          : baseDescription;
        
        // 4. Проверяем, изменился ли запрос (сравниваем базовое описание)
        const hasDescriptionChanged = baseDescription !== lastSubmittedDescription;
        
        const abTestMode = getABTestMode();
        
        console.log('🔍 AnalysisForm - Отправляем данные:', {
          ...formData,
          expert: finalExpert,
          person: finalPerson,
          abTestMode
        });
        console.log('🔍 AnalysisForm - Изменение запроса:', {
          lastSubmitted: lastSubmittedDescription,
          current: finalDescription,
          hasChanged: hasDescriptionChanged
        });
        
        // 5. Всегда используем фоновый анализ (по умолчанию)
        onSubmit({
          ...formData,
          description: finalDescription, // Используем финальное описание с персональными данными
          expert: finalExpert,
          person: finalPerson,
          abTestMode
        });
        
        // 6. Обновляем последний отправленный запрос (сохраняем базовое описание)
        setLastSubmittedDescription(baseDescription);
        setIsTyping(false);
        
        // Сбрасываем ID обработанной ошибки при новом анализе
        setProcessedFailedTaskId(null);
      } catch (error) {
        console.error('❌ AnalysisForm - Ошибка при валидации эксперта/личности:', error);
        // В случае ошибки валидации все равно запускаем анализ
        // (статистика уже отправлена в сервисе)
        
        // Определяем финального эксперта
        let finalExpert = formData.expert;
        if (formData.expert === 'custom' && customExpert.trim()) {
          finalExpert = customExpert.trim();
        }
        
        // Определяем финальную личность
        let finalPerson = formData.person;
        if (formData.person === 'custom' && customPerson.trim()) {
          finalPerson = customPerson.trim();
        }
        
        const finalDescription = formData.description?.trim() || '';
        
        const abTestMode = getABTestMode();
        
        onSubmit({
          ...formData,
          expert: finalExpert,
          person: finalPerson,
          abTestMode
        });
        
        // Обновляем последний отправленный запрос
        setLastSubmittedDescription(finalDescription);
        setIsTyping(false);
        
        // Сбрасываем ID обработанной ошибки при новом анализе
        setProcessedFailedTaskId(null);
      }
    }
  }, [formData, customExpert, customPerson, isDescriptionValid, lastSubmittedDescription, onSubmit]);

  const handleChange = (field: keyof AnalysisFormData, value: string) => {
    setFormData(prev => ({ 
      ...prev, 
      [field]: field === 'age' 
        ? (value === '' ? undefined : parseInt(value, 10))
        : (value === '' ? undefined : value)
    }));
    
    // Если пользователь начал вводить описание и есть активный анализ, отменяем его
    if (field === 'description' && backgroundAnalysisStatus && !isTyping) {
      console.log('🔍 AnalysisForm - Пользователь начал вводить, отменяем анализ');
      setIsTyping(true);
      // Вызываем функцию отмены, если она предоставлена
      if (onCancel) {
        onCancel();
      }
    }
    
    // Показываем поле для произвольного эксперта, если выбран "Другой эксперт..."
    if (field === 'expert') {
      setShowCustomExpert(value === 'custom');
      // Не сворачиваем блок опций, только управляем показом кастомного поля
      if (value !== 'custom') {
        setCustomExpert('');
      }
      // Если выбран эксперт, сбрасываем личность (только если не первый рендер)
      if (value && value.trim() && !isFirstRender.current) {
        setFormData(prev => ({ ...prev, person: undefined }));
        setShowCustomPerson(false);
        setCustomPerson('');
      }
    }
    
    // Показываем поле для произвольной личности, если выбрана "Другая личность..."
    if (field === 'person') {
      setShowCustomPerson(value === 'custom');
      // Не сворачиваем блок опций, только управляем показом кастомного поля
      if (value !== 'custom') {
        setCustomPerson('');
      }
      // Если выбрана личность, сбрасываем эксперта (только если не первый рендер)
      if (value && value.trim() && !isFirstRender.current) {
        setFormData(prev => ({ ...prev, expert: undefined }));
        setShowCustomExpert(false);
        setCustomExpert('');
      }
    }
  };

  // Функции для обработки модального окна с выбором вопроса
  const handleQuestionChoice = (choice: string) => {
    setQuestionChoice(choice);
    if (choice === 'none') {
      setCustomQuestion('');
    }
  };

  const handleExampleQuestionSelect = (question: string) => {
    setQuestionChoice(question); // Теперь сохраняем сам вопрос как choice
  };

  const handleCustomQuestionChange = (value: string) => {
    setCustomQuestion(value);
    setQuestionChoice('custom');
  };

  const handleQuestionModalSubmit = () => {
    let updatedFormData = { ...formData };

    // Проверяем, является ли questionChoice одним из примеров вопросов
    const isExampleQuestion = backgroundAnalysisStatus?.data?.examples?.includes(questionChoice || '');
    
    if (isExampleQuestion && questionChoice) {
      updatedFormData.question = questionChoice;
      updatedFormData.noQuestion = false;
    } else if (questionChoice === 'custom' && customQuestion.trim()) {
      updatedFormData.question = customQuestion.trim();
      updatedFormData.noQuestion = false;
    } else if (questionChoice === 'none') {
      updatedFormData.question = undefined;
      updatedFormData.noQuestion = true;
    }

    setShowQuestionModal(false);

    // Обновляем состояние формы, чтобы отобразить выбранный вопрос
    setFormData(updatedFormData);

    // Определяем финального эксперта и персону (копируем логику из handleSubmit)
    let finalExpert = updatedFormData.expert;
    if (updatedFormData.expert === 'custom' && customExpert.trim()) {
      finalExpert = customExpert.trim();
    }

    let finalPerson = updatedFormData.person;
    if (updatedFormData.person === 'custom' && customPerson.trim()) {
      finalPerson = customPerson.trim();
    }

    const finalDescription = updatedFormData.description.trim();

    // Повторно отправляем анализ с выбранным вопросом или без вопроса
    const dataToSubmit = {
      ...updatedFormData,
      description: finalDescription,
      expert: finalExpert,
      person: finalPerson
    };
    onSubmit(dataToSubmit);

    setLastSubmittedDescription(finalDescription);
    
    // Сбрасываем ID обработанной ошибки при новом анализе
    setProcessedFailedTaskId(null);
  };

  const handleQuestionModalCancel = () => {
    setShowQuestionModal(false);
    setQuestionChoice(null);
    setCustomQuestion('');
    
    // Сбрасываем lastSubmittedDescription, чтобы можно было запустить анализ заново
    setLastSubmittedDescription('');
    
    // Очищаем validation error статус в родительском компоненте
    if (onClearValidationError) {
      onClearValidationError();
    }
  };

  const handleExampleClick = () => {
    // Устанавливаем флаг, что это программное изменение
    isExampleClick.current = true;
    
    const randomExample = getRandomExample(t, formData.description);
    
    // Обновляем состояние напрямую, минуя handleChange, чтобы не срабатывала логика отмены анализа
    setFormData(prev => ({ 
      ...prev, 
      description: randomExample
    }));
    
    // Сбрасываем lastSubmittedDescription, чтобы можно было запустить анализ с новым примером
    setLastSubmittedDescription('');
    
    // Сбрасываем флаг после обновления состояния (даем время для обработки всех useEffect)
    setTimeout(() => {
      isExampleClick.current = false;
    }, 100);
  };

  const isDark = theme === 'dark';
  
  return (
    <>
      <FullScreenLoader backgroundAnalysisStatus={backgroundAnalysisStatus} isLoading={isLoading} theme={theme} />
    <div className={`backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border relative z-20 ${
      isDark 
        ? 'bg-slate-800/90 border-slate-700/50' 
        : 'bg-white/90 border-white/20'
    }`}>
      <div className="flex items-center mb-3 sm:mb-4">
        <Brain className={`h-5 w-5 sm:h-6 sm:w-6 mr-2 ${
          isDark ? 'text-blue-400' : 'text-primary-600'
        }`} />
        <h2 className={`text-lg sm:text-xl font-semibold ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          {t('formStartNewAnalysis')}
        </h2>
        <div className={`ml-auto flex items-center text-xs sm:text-sm ${
          isDark ? 'text-blue-400' : 'text-primary-600'
        }`}>
          <Zap className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
          Powered by AI
        </div>
      </div>
      
      {/* Отображение ошибки анализа */}
      {error && (
        <div className={`mb-4 p-3 border rounded-lg ${
          isDark 
            ? 'bg-red-900/20 border-red-800/50' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className={`text-sm font-medium ${
                isDark ? 'text-red-300' : 'text-red-800'
              }`}>{t('formAnalysisError')}</h3>
              <p className={`text-sm mt-1 ${
                isDark ? 'text-red-200' : 'text-red-700'
              }`}>
                {error}
              </p>
            </div>
            {onClearValidationError && (
              <button
                onClick={onClearValidationError}
                className={`ml-3 flex-shrink-0 p-2 rounded-lg transition-colors -mt-1 ${
                  isDark 
                    ? 'text-red-400 hover:text-red-300 hover:bg-red-900/30' 
                    : 'text-red-400 hover:text-red-600 hover:bg-red-100'
                }`}
                title={t('formClose')}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div>
          <label htmlFor="description" className={`block text-xs sm:text-sm font-medium mb-2 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {t('formDescribeSituation')} *
          </label>
          <div className="relative">
            <textarea
              id="description"
              className={`w-full px-3 sm:px-4 py-1 sm:py-2 sm:py-3 pb-6 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none relative z-20 text-sm sm:text-base transition-all duration-300 ${
                isDark
                  ? currentLength > MAX_DESCRIPTION_LENGTH 
                    ? 'border-red-500 ring-2 ring-red-400 bg-red-900/20 text-white placeholder-gray-400' 
                    : trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION_LENGTH
                    ? 'border-yellow-500 ring-2 ring-yellow-400 bg-yellow-900/20 text-white placeholder-gray-400'
                    : 'border-slate-600 bg-slate-700/50 text-white placeholder-gray-400'
                  : currentLength > MAX_DESCRIPTION_LENGTH 
                    ? 'border-red-400 ring-2 ring-red-300 bg-red-50' 
                    : trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION_LENGTH
                    ? 'border-yellow-400 ring-2 ring-yellow-300 bg-yellow-50'
                    : 'border-gray-300'
              }`}
              placeholder={t('formPlaceholder')}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              maxLength={MAX_DESCRIPTION_LENGTH}
              rows={3}
              required
            />
            <div className="absolute bottom-2 right-2 flex items-center gap-1.5 z-30">
              <div className={`text-xs opacity-60 pointer-events-none ${
                isDark
                  ? currentLength > MAX_DESCRIPTION_LENGTH ? 'text-red-400' 
                  : trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION_LENGTH ? 'text-yellow-400'
                  : 'text-gray-400'
                  : currentLength > MAX_DESCRIPTION_LENGTH ? 'text-red-600' 
                  : trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION_LENGTH ? 'text-yellow-600'
                  : 'text-gray-400'
              }`}>
                {currentLength}/{MAX_DESCRIPTION_LENGTH}
              </div>
              <button 
                type="button"
                onClick={handleExampleClick}
                disabled={isLoading || (backgroundAnalysisStatus !== null && backgroundAnalysisStatus !== undefined && backgroundAnalysisStatus.status !== 'completed' && backgroundAnalysisStatus.status !== 'failed')}
                className={`p-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto rounded ${
                  isDark 
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-slate-700' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title={t('formExamplesTitle')}
              >
                <Wand2 className="w-3 h-3" />
              </button>
            </div>
          </div>
          <p className={`text-xs mt-1 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION_LENGTH ? (
              <span className={isDark ? 'text-yellow-400' : 'text-yellow-600'}>
                {t('formMinLengthHint', { min: MIN_DESCRIPTION_LENGTH, remaining: MIN_DESCRIPTION_LENGTH - trimmedLength })}
              </span>
            ) : (
              t('formDescribeHintLong')
            )}
          </p>
        </div>

        {/* Отображение выбранного вопроса */}
        {(formData.question || formData.noQuestion) && (
          <div className={`border rounded-lg p-3 ${
            isDark 
              ? 'bg-blue-900/20 border-blue-800/50' 
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className={`h-5 w-5 ${
                  isDark ? 'text-blue-400' : 'text-blue-400'
                }`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h4 className={`text-sm font-medium ${
                  isDark ? 'text-blue-300' : 'text-blue-800'
                }`}>
                  {formData.noQuestion ? t('formGeneralAnalysis') : t('formAnalysisWithQuestion')}
                </h4>
                <p className={`text-sm mt-1 ${
                  isDark ? 'text-blue-200' : 'text-blue-700'
                }`}>
                  {formData.noQuestion 
                    ? t('formGeneralAnalysisDesc')
                    : `"${formData.question}"`
                  }
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Прерываем анализ, если он запущен
                  if (onAbortAnalysis) {
                    onAbortAnalysis();
                  }
                  
                  // Очищаем вопрос из формы
                  setFormData(prev => ({ 
                    ...prev, 
                    question: undefined, 
                    noQuestion: false 
                  }));
                  
                  // Сбрасываем lastSubmittedDescription, чтобы можно было запустить анализ заново
                  setLastSubmittedDescription('');
                }}
                className={`flex-shrink-0 ml-3 transition-colors ${
                  isDark 
                    ? 'text-blue-400 hover:text-blue-300' 
                    : 'text-blue-400 hover:text-blue-600'
                }`}
                title={t('formRemoveQuestion')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Блок выбора эксперта */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowExpertSection(!showExpertSection)}
            className={`w-full flex items-center justify-between text-left text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-3 border transition-all duration-200 ${
              isDark
                ? 'text-gray-300 hover:text-white border-slate-600 hover:border-slate-500 bg-slate-700/50 hover:bg-slate-700'
                : 'text-gray-700 hover:text-gray-900 border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2" />
              <span>{t('formOptions')}</span>
            </div>
            {showExpertSection ? (
              <ChevronUp className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            ) : (
              <ChevronDown className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            )}
          </button>
          
          {showExpertSection && (
            <div className="mt-3 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="person-dropdown">
                  <label className={`block text-xs sm:text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('formChoosePerson')}
                  </label>
                  <select
                    id="person"
                    className={`w-full px-4 py-3 text-sm text-left rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDark
                        ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500 text-white'
                        : 'bg-white border-gray-300 hover:border-gray-400'
                    }`}
                    value={formData.person || ''}
                    onChange={(e) => handleChange('person', e.target.value)}
                    disabled={listsLoading}
                  >
                    <option value="">
                      {listsLoading ? t('formLoadingPersons') : t('formChoosePersonPlaceholder')}
                    </option>
                    {persons.map((person) => (
                      <option key={person.value} value={person.value}>
                        {person.label}
                      </option>
                    ))}
                  </select>
                  <p className={`text-xs mt-1 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {t('formChoosePersonHint')}
                  </p>

                  {/* Поле для произвольной личности */}
                  {showCustomPerson && (
                    <div className="mt-3">
                      <label htmlFor="customPerson" className={`block text-xs sm:text-sm font-medium mb-1 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Укажите личность
                      </label>
                      <input
                        id="customPerson"
                        type="text"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                          isDark
                            ? `bg-slate-700/50 border-slate-600 text-white placeholder-gray-400 ${
                                customPerson && !validateExpert(customPerson) ? 'border-red-500' : ''
                              }`
                            : `border-gray-300 ${
                                customPerson && !validateExpert(customPerson) ? 'border-red-300' : ''
                              }`
                        }`}
                        placeholder={t('formSpecifyPersonPlaceholder')}
                        value={customPerson}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Автоматически очищаем невалидные символы
                          const sanitizedValue = sanitizeExpert(value);
                          setCustomPerson(sanitizedValue);
                        }}
                        maxLength={100}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <p className={`text-xs ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {t('formSpecifyPersonHint')}
                        </p>
                        <span className={`text-xs ${
                          isDark
                            ? customPerson && !validateExpert(customPerson) ? 'text-red-400' : 'text-gray-400'
                            : customPerson && !validateExpert(customPerson) ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {customPerson && !validateExpert(customPerson) ? t('formCharsRemoved') : t('formCharsCount', { count: customPerson?.length ?? 0 })}
                        </span>
                      </div>
                      {customPerson && !validateExpert(customPerson) && (
                        <p className={`text-xs mt-1 ${
                          isDark ? 'text-red-400' : 'text-red-600'
                        }`}>
                          ⚠️ {t('formAllowedChars')}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="expert-dropdown">
                  <label className={`block text-xs sm:text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('formOrChooseExpert')}
                  </label>
                  <select
                    id="expert"
                    className={`w-full px-4 py-3 text-sm text-left rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDark
                        ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500 text-white'
                        : 'bg-white border-gray-300 hover:border-gray-400'
                    }`}
                    value={formData.expert || ''}
                    onChange={(e) => handleChange('expert', e.target.value)}
                    disabled={listsLoading}
                  >
                    <option value="">
                      {listsLoading ? t('formLoadingExperts') : t('formChooseExpertPlaceholder')}
                    </option>
                    {experts.map((expert) => (
                      <option key={expert.value} value={expert.value}>
                        {expert.label}
                      </option>
                    ))}
                  </select>
                  <p className={`text-xs mt-1 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {t('formChooseExpertHint')}
                  </p>

                  {/* Поле для произвольного эксперта */}
                  {showCustomExpert && (
                    <div className="mt-3">
                      <label htmlFor="customExpert" className={`block text-xs sm:text-sm font-medium mb-1 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {t('formSpecifyExpert')}
                      </label>
                      <input
                        id="customExpert"
                        type="text"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                          isDark
                            ? `bg-slate-700/50 border-slate-600 text-white placeholder-gray-400 ${
                                customExpert && !validateExpert(customExpert) ? 'border-red-500' : ''
                              }`
                            : `border-gray-300 ${
                                customExpert && !validateExpert(customExpert) ? 'border-red-300' : ''
                              }`
                        }`}
                        placeholder={t('formSpecifyExpertPlaceholder')}
                        value={customExpert}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Автоматически очищаем невалидные символы
                          const sanitizedValue = sanitizeExpert(value);
                          setCustomExpert(sanitizedValue);
                        }}
                        maxLength={100}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <p className={`text-xs ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {t('formSpecifyExpertHint')}
                        </p>
                        <span className={`text-xs ${
                          isDark
                            ? customExpert && !validateExpert(customExpert) ? 'text-red-400' : 'text-gray-400'
                            : customExpert && !validateExpert(customExpert) ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {customExpert && !validateExpert(customExpert) ? t('formCharsRemoved') : t('formCharsCount', { count: customExpert?.length ?? 0 })}
                        </span>
                      </div>
                      {customExpert && !validateExpert(customExpert) && (
                        <p className={`text-xs mt-1 ${
                          isDark ? 'text-red-400' : 'text-red-600'
                        }`}>
                          ⚠️ {t('formAllowedChars')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Дополнительные поля */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* Пол */}
                <div>
                  <label htmlFor="gender" className={`block text-xs sm:text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('formGender')}
                  </label>
                  <select
                    id="gender"
                    className={`w-full px-3 py-2 text-sm text-left rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500 text-white'
                        : 'bg-white border-gray-300 hover:border-gray-400'
                    }`}
                    value={formData.gender || ''}
                    onChange={(e) => handleChange('gender', e.target.value || '')}
                  >
                    <option value="">{t('formGenderNotSpecified')}</option>
                    <option value="мужской">{t('formGenderMale')}</option>
                    <option value="женский">{t('formGenderFemale')}</option>
                  </select>
                </div>

                {/* Возраст */}
                <div>
                  <label htmlFor="age" className={`block text-xs sm:text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('formAge')}
                  </label>
                  <input
                    id="age"
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                      isDark
                        ? 'bg-slate-700/50 border-slate-600 text-white placeholder-gray-400'
                        : 'border-gray-300'
                    }`}
                    placeholder={t('formAgePlaceholder')}
                    value={formData.age ? t('formAgeYears', { age: formData.age }) : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value) {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue > 0 && numValue <= 120) {
                          handleChange('age', numValue.toString());
                        }
                      } else {
                        handleChange('age', '');
                      }
                    }}
                  />
                </div>

                {/* Семейное положение */}
                <div>
                  <label htmlFor="maritalStatus" className={`block text-xs sm:text-sm font-medium mb-1 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('formMaritalStatus')}
                  </label>
                  <select
                    id="maritalStatus"
                    className={`w-full px-3 py-2 text-sm text-left rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500 text-white'
                        : 'bg-white border-gray-300 hover:border-gray-400'
                    }`}
                    value={formData.maritalStatus || ''}
                    onChange={(e) => handleChange('maritalStatus', e.target.value || '')}
                  >
                    <option value="">{t('formMaritalStatusNotSpecified')}</option>
                    <option value="холост/не замужем">{t('formMaritalStatusSingleOption')}</option>
                    <option value="женат/замужем">{t('formMaritalStatusMarriedOption')}</option>
                    <option value="в разводе">{t('formMaritalStatusDivorced')}</option>
                    <option value="вдовец/вдова">{t('formMaritalStatusWidowed')}</option>
                    <option value="в гражданском браке">{t('formMaritalStatusCivil')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            type="submit"
            disabled={isLoading || !isDescriptionValid || !csrfInitialized || (!error && (formData.description?.trim() || '') === lastSubmittedDescription && lastSubmittedDescription !== '')}
            className={`w-full disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg py-3 sm:py-4 px-6 sm:px-8 relative z-10 overflow-hidden ${
              backgroundAnalysisStatus && backgroundAnalysisStatus.status !== 'completed' && backgroundAnalysisStatus.status !== 'failed'
                ? 'btn bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 active:bg-primary-800'
                : 'btn-primary'
            }`}
          >
            {/* Фоновый прогресс-бар */}
            {backgroundAnalysisStatus && backgroundAnalysisStatus.status !== 'completed' && backgroundAnalysisStatus.status !== 'failed' && (
              <div className="absolute inset-0 bg-primary-400 transition-all duration-300 z-10" 
                   style={{ 
                     width: backgroundAnalysisStatus.timeSinceStart && backgroundAnalysisStatus.averageAnalysisTime 
                       ? `${Math.min(100, (backgroundAnalysisStatus.timeSinceStart / backgroundAnalysisStatus.averageAnalysisTime) * 100)}%` 
                       : '0%' 
                   }} />
            )}

            {/* Содержимое кнопки */}
            <div className="relative z-10 flex items-center justify-center">
              {!csrfInitialized ? (
                <>
                  <div className={`rounded-full mr-2 ${
                    isDark ? 'bg-slate-700' : 'bg-white'
                  }`}>
                    <img 
                      src="/images/preloader-plus-minus.svg" 
                      alt={t('formLoadingAlt')} 
                      className="h-4 w-4 sm:h-5 sm:w-5"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  {t('formSecurityInit')}
                </>
              ) : backgroundAnalysisStatus ? (
                <>
                  <div className={`rounded-full mr-2 ${
                    isDark ? 'bg-slate-700' : 'bg-white'
                  }`}>
                    <img 
                      src="/images/preloader-plus-minus.svg" 
                      alt={t('formLoadingAlt')} 
                      className="h-4 w-4 sm:h-5 sm:w-5"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div>
                    <div className="font-medium text-center">
                      {backgroundAnalysisStatus.status === 'pending' ? t('formQueueWaiting') : t('formAnalysisRunning')}
                      {backgroundAnalysisStatus.estimatedTimeRemaining !== null && backgroundAnalysisStatus.estimatedTimeRemaining !== undefined && backgroundAnalysisStatus.estimatedTimeRemaining > 0 && (
                        <span className="text-xs opacity-90 ml-2">
                          • {t('formTimeRemainingShort')}: {formatTime(backgroundAnalysisStatus.estimatedTimeRemaining)}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : isLoading ? (
                <>
                  <div className={`rounded-full mr-2 ${
                    isDark ? 'bg-slate-700' : 'bg-white'
                  }`}>
                    <img 
                      src="/images/preloader-plus-minus.svg" 
                      alt={t('formLoadingAlt')} 
                      className="h-4 w-4 sm:h-5 sm:w-5"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  {t('formSendingToQueue')}
                </>
              ) : (
                <>
                  <Brain className="w-5 h-5 mr-2" />
                  {error && (formData.description?.trim() || '') === lastSubmittedDescription ? t('formRetryAnalysis') : ((formData.description?.trim() || '') === lastSubmittedDescription && lastSubmittedDescription !== '' ? t('formRequestUnchanged') : t('formGetAISolution'))}
                </>
              )}
            </div>
          </button>
        </div>
        <div className={`mt-3 text-xs text-center ${
          isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>
          {t('formTryFree')}
        </div>
        

      </form>

      {/* Disclaimer */}
      <Disclaimer className={`mt-4 pt-4 border-t ${
        isDark ? 'border-slate-700' : 'border-gray-200'
      }`} theme={theme} />

      {/* Модальное окно выбора вопроса */}
      {showQuestionModal && 
       backgroundAnalysisStatus?.status === 'failed' &&
       backgroundAnalysisStatus?.data?.type === 'validation_error' &&
       backgroundAnalysisStatus?.data?.examples && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
            isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'
          }`}>
            <div className="p-6">
              {/* Заголовок */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <AlertCircle className="h-6 w-6 text-amber-500 mr-3" />
                  <div>
                    <h3 className={`text-lg font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('formCouldNotDetermineQuestion')}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {backgroundAnalysisStatus.data.error || t('formNoQuestionError')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleQuestionModalCancel}
                  className={`transition-colors ${
                    isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Опции выбора */}
              <div className="space-y-3">
                {/* Примеры вопросов на верхнем уровне */}
                {backgroundAnalysisStatus.data.examples && backgroundAnalysisStatus.data.examples.length > 0 && (
                  <>
                    {backgroundAnalysisStatus.data.examples.map((example, index) => (
                      <div key={index} className="p-3">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="radio"
                            name="questionChoice"
                            value={example}
                            checked={questionChoice === example}
                            onChange={() => handleExampleQuestionSelect(example)}
                            className="text-primary-600 focus:ring-primary-500 mt-1"
                          />
                          <span className={`ml-3 text-base font-medium leading-relaxed ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {example}
                          </span>
                        </label>
                      </div>
                    ))}
                  </>
                )}

                {/* Поле ввода своего вопроса на верхнем уровне */}
                <div className="p-3">
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="questionChoice"
                      value="custom"
                      checked={questionChoice === 'custom'}
                      onChange={() => handleQuestionChoice('custom')}
                      className="text-primary-600 focus:ring-primary-500 mt-3"
                    />
                    <div className="ml-3 flex-1">
                      <input
                        type="text"
                        value={customQuestion}
                        onChange={(e) => handleCustomQuestionChange(e.target.value)}
                        onFocus={() => handleQuestionChoice('custom')}
                        placeholder={t('formQuestionPlaceholder')}
                        maxLength={100}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm ${
                          isDark
                            ? 'bg-slate-700/50 border-slate-600 text-white placeholder-gray-400'
                            : 'border-gray-300'
                        }`}
                      />
                      <p className={`text-xs mt-1 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {t('formCharsCount', { count: customQuestion.length })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Опция: анализ без вопроса */}
                <div className="p-3">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      name="questionChoice"
                      value="none"
                      checked={questionChoice === 'none'}
                      onChange={() => handleQuestionChoice('none')}
                      className="text-primary-600 focus:ring-primary-500 mt-1"
                    />
                    <div className="ml-3">
                      <span className={`font-medium ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {t('formAnalysisWithoutQuestion')}
                      </span>
                      <p className={`text-sm mt-1 ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {t('formAnalysisWithoutQuestionDesc')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Кнопки действий */}
              <div className={`flex justify-end space-x-3 mt-6 pt-4 border-t ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <button
                  onClick={handleQuestionModalCancel}
                  className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                    isDark
                      ? 'text-gray-300 bg-slate-700 border border-slate-600 hover:bg-slate-600'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('formCancelAnalysis')}
                </button>
                <button
                  onClick={handleQuestionModalSubmit}
                  disabled={
                    !questionChoice || 
                    (questionChoice === 'custom' && !customQuestion.trim())
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('formContinueAnalysis')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
} 